import React, { useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Tooltip, Tag } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateLiftSelected,
  updateBankSelected,
  updateModalControlPanelTab,
  updateOpenModalControlPanelLift,
  updateControlPanelDrawerPlacement,
  updateChannelId,
  updateServerId,
} from "../../../redux/monitoring/monitoringSlice";
// CUSTOM HOOKS
import { useLastLiftMessage, useDebounceMap } from "../socketMessageState/BankMessagesContext";
import useDirectionDebounce from "../iconDefinition/useDirectionDebounce";
// COMPONENTS
import IconDefinition from "../iconDefinition/IconDefinition";
import IconDefinitionEscalatorDefault from "../iconDefinition/IconDefinitionEscalatorDefault";
// INTERFACES
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { RootState } from "../../../app/store";
import { BankInterface } from "../../../interfaces/Bank.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
import { defaultLiftPanelTab } from "../../../types/monitoringAccess";
import { useCanOpenControlPanel } from "../../../customHooks/useScopedPermission";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
import { usageToDoorIconFiltered } from "../../../utils/bitmaskToIcon";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { stringToUuidT } from "../../../utils/uuid";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";

function MonitoringLiftStatusView({
  lift,
  bank,
  serverId,
  channelId,
  bankHasMixedDoors,
}: {
  lift: LiftInterface;
  bank: BankInterface;
  serverId: string;
  channelId: string;
  bankHasMixedDoors: boolean;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const doorUnknownAsOpening = useSelector(
    (state: RootState) => state.monitoring.doorUnknownAsOpening,
  );
  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(() => stringToUuidT(String(lift.id ?? "")), [lift.id]);
  const rawLiftMsg = useLastLiftMessage(liftUuid);
  const socketMessageLift: SocketMessageInterface = rawLiftMsg ?? BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = socketMessageLift.faults;
  const debounceMap = useDebounceMap();

  // Same un-hydrated-vs-through-car rationale as MonitoringLiftTopView.tsx's
  // hasFrontDoor/hasRearDoor (the sibling STATUS_VIEW-mode view of this lift) — see
  // there for why the rearDoor fallback is `?? false`, not `?? true`.
  const hasFrontDoor = useMemo(
    () => lift.liftFloors?.some((lf) => lf.frontDoor) ?? true,
    [lift.liftFloors],
  );
  const hasRearDoor = useMemo(
    () => lift.liftFloors?.some((lf) => lf.rearDoor) ?? false,
    [lift.liftFloors],
  );
  const doorIcon = useMemo(
    () =>
      usageToDoorIconFiltered(
        socketMessageLift.usage,
        hasFrontDoor,
        hasRearDoor,
        doorUnknownAsOpening,
        bankHasMixedDoors,
      ),
    [socketMessageLift.usage, doorUnknownAsOpening, hasFrontDoor, hasRearDoor, bankHasMixedDoors],
  );
  const { directionIcon, lastDirectionIcon } = useDirectionDebounce(
    socketMessageLift.usage,
    debounceMap,
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getTabKey = () => {
    // Actions is the default tab for the drawer whenever it's available.
    if (hasPermission(Permission.MonitorControlPanelActions, permissionSet ?? [])) {
      return "ACTIONS";
    }
    if (hasFaults) {
      return "FAULTS";
    }
    return defaultLiftPanelTab(permissionSet ?? []);
  };

  const getDrawerPlacement = (e: any) => {
    const clickX = e.clientX;
    const screenWidth = window.innerWidth;
    if (clickX > screenWidth / 2) {
      dispatch(updateControlPanelDrawerPlacement("left"));
    } else {
      dispatch(updateControlPanelDrawerPlacement("right"));
    }
  };

  const isEscalator = bank.type === LiftType.ESCALATOR || bank.type === LiftType.MOVINGWALK;

  // The lift name opens the panel only when at least one of the panel's tabs
  // would render for this role (see CONTROL_PANEL_TAB_GATES); MASTER ADMIN always.
  // Deliberately NOT tied to the Banks switch: the Roles editor's Monitoring
  // Dashboard Banks block shows the lifts, it does not make their names clickable.
  const canOpenLiftControlPanel = useCanOpenControlPanel("lift");

  const onClickOpenModalElevator = (e: any) => {
    // With no tab to show, the lift name is inert; the per-tab permissions only
    // matter once the panel IS open.
    if (!canOpenLiftControlPanel) return;
    getDrawerPlacement(e);
    dispatch(
      updateModalControlPanelTab({
        liftType: isEscalator ? LiftType[LiftType.ESCALATOR] : LiftType[LiftType.ELEVATOR],
        tab: getTabKey(),
      }),
    );
    dispatch(updateLiftSelected(lift));
    dispatch(updateBankSelected(bank));
    dispatch(updateChannelId(channelId));
    dispatch(updateServerId(serverId));
    dispatch(updateOpenModalControlPanelLift(true));
  };

  // ************************************************ */
  // COMPONENT ************************************** */
  const displayDirection = directionIcon || lastDirectionIcon;
  const directionDimmed = !directionIcon && !!lastDirectionIcon;
  const fallbackIcon = bankHasMixedDoors ? "elDoubleOutService" : "elOutService";

  const renderIcon = () => {
    if (isEscalator) {
      return <IconDefinitionEscalatorDefault iconSize={28} mapViewClass="" lift={lift} />;
    }
    // Show disconnected icon only when we have received a message that says disconnected.
    // Before any message arrives (rawLiftMsg === undefined) show nothing — it is a loading
    // state, not a disconnected state.
    if (rawLiftMsg && !socketMessageLift.connected) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType={LiftType[LiftType.ELEVATOR]}
          icon="elOutIndicator"
          iconSize={16}
        />
      );
    }
    const primaryIcon = doorIcon || (!displayDirection ? fallbackIcon : null);
    return (
      <>
        {primaryIcon && (
          <IconDefinition
            isMap={false}
            expanded={false}
            liftType={LiftType[LiftType.ELEVATOR]}
            icon={primaryIcon}
            iconSize={16}
          />
        )}
        {displayDirection && (
          <span style={{ opacity: directionDimmed ? 0.3 : 1 }}>
            <IconDefinition
              isMap={false}
              expanded={false}
              liftType={LiftType[LiftType.ELEVATOR]}
              icon={displayDirection}
              iconSize={16}
            />
          </span>
        )}
      </>
    );
  };

  return (
    <div className="mt-1">
      <div
        className={`monitoringView__topViewContainer__${theme}  ${
          hasFaults ? `monitoringView__dangerElevator   ` : ""
        }${hasFaults ? `monitoringView__dangerElevator__top ` : ""}${
          hasFaults ? `monitoringView__dangerElevator__bottom` : ""
        }  `}
        style={{
          height: "30px",
          width: "45px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginLeft: "6px",
          marginRight: "6px",
          marginTop: "-10px",
        }}
      >
        {renderIcon()}
      </div>
      {/** ELEVATOR NAME */}
      <div
        role="button"
        tabIndex={0}
        onKeyDown={emptyFunctionDeclaration}
        onClick={onClickOpenModalElevator}
        style={{
          marginTop: "5px",
          fontSize: "9px",
          fontWeight: "bolder",
          textAlign: "center",
        }}
      >
        <div>
          <Tooltip placement="top" title={String(lift.name ?? "")}>
            <span className={hasFaults ? "generalStyles__error" : ""}>
              {String(lift.name ?? "")
                .toUpperCase()
                .substring(0, 6)}
            </span>
          </Tooltip>
        </div>
        {hasFaults && (
          <div>
            <Tag
              style={{
                marginLeft: "-3px",
                marginTop: "3px",
                fontSize: "8px",
                width: "36px",
              }}
              color="red"
            >
              FAULT
            </Tag>
          </div>
        )}
        {socketMessageLift.serviceMode &&
          (() => {
            const display = getServiceModeDisplay(
              socketMessageLift.serviceMode.messageType,
              socketMessageLift.serviceMode.message?.message?.message ?? null,
            );
            if (!display) return null;
            return (
              <div>
                <Tooltip placement="top" title={display.displayName}>
                  <Tag
                    style={{
                      marginLeft: "-3px",
                      marginTop: "3px",
                      fontSize: "8px",
                      width: "36px",
                    }}
                    color={display.color}
                  >
                    {display.abbreviation.substring(0, 4)}
                  </Tag>
                </Tooltip>
              </div>
            );
          })()}
      </div>
    </div>
  );
}

export default React.memo(MonitoringLiftStatusView);
